/**
 * Selectors / computed values dla transakcji.
 * Pure functions — można testować bez Reacta.
 */
import type { Transaction } from './transactionsStore';

/** Zwraca saldo portfela (running): income+, expense-, transfer ±.
 *  Jeśli aggregateAll=true → ignoruje walletId i sumuje całą rodzinę
 *  (semantyka portfela typu 'shared' / "Wspólny"). */
export function computeWalletBalance(
  transactions: Transaction[],
  walletId: string,
  aggregateAll = false,
): number {
  let balance = 0;
  for (const tx of transactions) {
    if (aggregateAll) {
      if (tx.type === 'income') balance += tx.amount;
      else if (tx.type === 'expense') balance -= tx.amount;
      // transfery wewnątrz rodziny się znoszą — pomijamy
      continue;
    }
    if (tx.type === 'income' && tx.walletId === walletId) balance += tx.amount;
    else if (tx.type === 'expense' && tx.walletId === walletId) balance -= tx.amount;
    else if (tx.type === 'transfer') {
      if (tx.walletId === walletId) balance -= tx.amount;
      else if (tx.toWalletId === walletId) balance += tx.amount;
    }
  }
  return balance;
}

/** 30-dniowy histogram salda (dla sparkline). aggregateAll j/w. */
export function computeWalletSparkline(
  transactions: Transaction[],
  walletId: string,
  days = 30,
  aggregateAll = false,
): number[] {
  const today = new Date();
  today.setHours(0, 0, 0, 0);
  const points: number[] = [];
  for (let i = 0; i < days; i++) {
    const dayEnd = new Date(today);
    dayEnd.setDate(today.getDate() - (days - 1 - i));
    dayEnd.setHours(23, 59, 59, 999);
    let balance = 0;
    for (const tx of transactions) {
      if (tx.date.getTime() > dayEnd.getTime()) continue;
      if (aggregateAll) {
        if (tx.type === 'income') balance += tx.amount;
        else if (tx.type === 'expense') balance -= tx.amount;
        continue;
      }
      if (tx.type === 'income' && tx.walletId === walletId) balance += tx.amount;
      else if (tx.type === 'expense' && tx.walletId === walletId) balance -= tx.amount;
      else if (tx.type === 'transfer') {
        if (tx.walletId === walletId) balance -= tx.amount;
        else if (tx.toWalletId === walletId) balance += tx.amount;
      }
    }
    points.push(balance);
  }
  return points;
}

/** Filtruj transakcje po: walletId (lub null = wszystkie), miesiącu, query. */
export interface FilterOptions {
  walletId: string | null;
  monthStart: Date;
  monthEnd: Date;
  searchQuery?: string;
}

export function filterTransactions(transactions: Transaction[], opts: FilterOptions): Transaction[] {
  const q = opts.searchQuery?.trim().toLowerCase();
  return transactions.filter((tx) => {
    if (tx.date < opts.monthStart || tx.date > opts.monthEnd) return false;
    if (opts.walletId && tx.walletId !== opts.walletId && tx.toWalletId !== opts.walletId) return false;
    if (q && !tx.note.toLowerCase().includes(q)) return false;
    return true;
  });
}

export interface MonthSummary {
  income: number;
  expense: number;
  balance: number;
}

/** walletId !== null → transfery liczone jako income (incoming) lub expense (outgoing) z punktu widzenia tego portfela.
 *  walletId === null (Wspólny / agregat rodziny) → transfery wewnętrzne się znoszą, pomijamy. */
export function computeMonthSummary(
  transactions: Transaction[],
  walletId: string | null = null,
): MonthSummary {
  let income = 0;
  let expense = 0;
  for (const tx of transactions) {
    if (tx.type === 'income') income += tx.amount;
    else if (tx.type === 'expense') expense += tx.amount;
    else if (tx.type === 'transfer' && walletId) {
      if (tx.toWalletId === walletId) income += tx.amount;
      else if (tx.walletId === walletId) expense += tx.amount;
    }
  }
  return { income, expense, balance: income - expense };
}
